import RAPIER from '@dimforge/rapier3d-compat';
import { DYNAMIC_GROUPS, STATIC_GROUPS } from './Layers';

export const PHYSICS_DT = 1 / 60;
export const GRAVITY = { x: 0, y: -9.81, z: 0 };

/** Island safety crust. Top face at y = 0. Covers Lost World vale at z ≈ −240. */
export const DEFAULT_GROUND_HX = 280;
export const DEFAULT_GROUND_HZ = 320;
const GROUND_HALF_Y = 0.5;

export type GroundDesc = {
  hx: number;
  hz: number;
  x: number;
  z: number;
};

export class PhysicsWorld {
  readonly world: RAPIER.World;
  readonly groundBody: RAPIER.RigidBody;
  readonly groundCollider: RAPIER.Collider;
  readonly ground: GroundDesc;

  constructor(ground: GroundDesc = { hx: DEFAULT_GROUND_HX, hz: DEFAULT_GROUND_HZ, x: 0, z: 0 }) {
    this.world = new RAPIER.World(GRAVITY);
    this.world.timestep = PHYSICS_DT;
    this.ground = ground;

    this.groundBody = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setTranslation(ground.x, -GROUND_HALF_Y, ground.z),
    );
    this.groundCollider = this.world.createCollider(
      RAPIER.ColliderDesc.cuboid(ground.hx, GROUND_HALF_Y, ground.hz).setCollisionGroups(
        STATIC_GROUPS,
      ),
      this.groundBody,
    );
  }

  addStaticCuboid(
    halfX: number,
    halfY: number,
    halfZ: number,
    x: number,
    y: number,
    z: number,
  ): { body: RAPIER.RigidBody; collider: RAPIER.Collider } {
    const body = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed().setTranslation(x, y, z));
    const collider = this.world.createCollider(
      RAPIER.ColliderDesc.cuboid(halfX, halfY, halfZ).setCollisionGroups(STATIC_GROUPS),
      body,
    );
    return { body, collider };
  }

  addStaticCylinder(
    halfHeight: number,
    radius: number,
    x: number,
    y: number,
    z: number,
  ): { body: RAPIER.RigidBody; collider: RAPIER.Collider } {
    const body = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed().setTranslation(x, y, z));
    const collider = this.world.createCollider(
      RAPIER.ColliderDesc.cylinder(halfHeight, radius).setCollisionGroups(STATIC_GROUPS),
      body,
    );
    return { body, collider };
  }

  addDynamicCuboid(
    halfX: number,
    halfY: number,
    halfZ: number,
    x: number,
    y: number,
    z: number,
  ): { body: RAPIER.RigidBody; collider: RAPIER.Collider } {
    const body = this.world.createRigidBody(RAPIER.RigidBodyDesc.dynamic().setTranslation(x, y, z));
    const collider = this.world.createCollider(
      RAPIER.ColliderDesc.cuboid(halfX, halfY, halfZ).setCollisionGroups(DYNAMIC_GROUPS),
      body,
    );
    return { body, collider };
  }

  addStaticHeightfield(
    nrows: number,
    ncols: number,
    heights: Float32Array,
    scale: { x: number; y: number; z: number },
    x: number,
    y: number,
    z: number,
  ): { body: RAPIER.RigidBody; collider: RAPIER.Collider } {
    const body = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed().setTranslation(x, y, z));
    const collider = this.world.createCollider(
      RAPIER.ColliderDesc.heightfield(
        nrows,
        ncols,
        heights,
        scale,
        RAPIER.HeightFieldFlags.FIX_INTERNAL_EDGES,
      ).setCollisionGroups(STATIC_GROUPS),
      body,
    );
    return { body, collider };
  }

  /** Drops a streamed body. Never call this on `groundBody`. */
  removeRigidBody(body: RAPIER.RigidBody): void {
    this.world.removeRigidBody(body);
  }

  step(): void {
    this.world.step();
  }

  free(): void {
    this.world.free();
  }
}
